import React, { FC, SyntheticEvent, useCallback, useContext, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { useAuth } from "@hooks/auth";
import { useIsDesktop } from "@hooks/layout";
import { useCountDownPlayback } from "@hooks/useCountDownPlayback";
import { FiltersType, navigateToCatalogWithQuery } from "@hooks/useFiltersQuery/utils";
import { useResetDownloadModalStep } from "@hooks/useResetDownloadModalStep";
import { TrackPhrases } from "@hooks/useToast/constants";
import { ToastStatuses, useToast } from "@hooks/useToast/useToast";
import { useWhitelistingPausedRedirect } from "@hooks/useWhitelistingPausedRedirect";
import { useWhitelistingSuspended } from "@hooks/useWhitelistingSuspended";
import { useWavesurfer } from "@hooks/wafeform";

import { createFavorites, deleteFavorites } from "@store/actions/favorites";
import { bottomTrack } from "@store/actions/track";
import { useAppDispatchUnwrap, useAppSelector } from "@store/hooks";
import { getFavoritesState } from "@store/selectors/getFavorites";
import { getBottomTrackSelector } from "@store/selectors/tracks";

import { TPlayerProps } from "@components/BottomPlayer/components/types";
import { formatTime } from "@components/RecoveryDialog/utils";
import { PlayerContext } from "@layouts/PlayerLayout/utils";
import { IFavorites } from "@models/models";
import { ERoutes } from "@utils/constants";
import { getTitleForDownloading } from "@utils/download";
import { makePath, makePathForTrack } from "@utils/makePath";
import { recordListenBeacon, recordMediaEvent } from "@utils/recordMediaEvent";
import { refusedWhileViewing } from "@utils/viewAs";

import { AccountType, useAccountType } from "@/contexts/AccountTypeContext";

import NewDownloadModal from "../../../DownloadModal";
import LicenseModal from "../../../LicenseModal";
import * as Styled from "../../styles";
import {
  EWaveSurferPlayer,
  getIsMuted,
  getVolume,
  MAX_VOLUME,
  MUTE_VOLUME,
  setMuted,
  setVolume,
  waveSurferPlayerReducer,
} from "../../utils";
import { DesktopLayout } from "../DesktopLayout";
import { MobileLayout } from "../MobileLayout";
import { isIOS, isSafari } from "./helpers";

type Props = {
  curated?: boolean;
  handleCloseTrack: () => void;
};

export const Player: FC<Props> = ({ handleCloseTrack, curated }) => {
  const isDesktop = useIsDesktop();
  const navigate = useNavigate();
  const location = useLocation();

  const { setPlay, data, setMedia, setOpenLoginDialog } = useContext(PlayerContext);
  const { isUserCanPlay } = useCountDownPlayback();

  const { id, isPlaying, slug } = data;

  const dispatch = useAppDispatchUnwrap();
  const { customToast } = useToast();

  const containerRef = useRef<HTMLDivElement>(null);
  const currentTimeRef = useRef<HTMLDivElement>(null);
  const progressRef = useRef<HTMLDivElement>(null);

  // ── Listen-time tracking (Playtime analytics) ──
  // Accumulate wall-clock seconds actually spent playing the current track, and
  // emit a `listen` event (carrying the total) when the listen ends — on pause,
  // finish, track change, or page close. `id` is held in the ref so a flush on
  // track-change reports the PREVIOUS track, not the one just loaded.
  const listenRef = useRef<{ id?: string; secs: number; since: number | null }>({ secs: 0, since: null });
  const ltAccrue = () => {
    const lt = listenRef.current;
    if (lt.since != null) {
      lt.secs += (performance.now() - lt.since) / 1000;
      lt.since = null;
    }
  };
  const ltFlush = (beacon = false) => {
    ltAccrue();
    const lt = listenRef.current;
    if (lt.id && lt.secs >= 1) {
      if (beacon) recordListenBeacon(lt.id, lt.secs);
      else recordMediaEvent(lt.id, "track", "listen", lt.secs);
    }
    lt.secs = 0;
  };

  const favoritesState = useAppSelector(getFavoritesState);
  const { bottomTrack: bottomTrackData } = useAppSelector(getBottomTrackSelector);
  // Entitlements, not plan identity: an account awaiting reactivation is on
  // the subscription plan but is not paying for it, so it browses and
  // downloads on free terms until it reactivates (ToU 17.4).
  const { entitlementType: accountType } = useAccountType();

  const getFavoritesTracks = (favorites: IFavorites[]) => {
    return new Set(favorites.map((obj) => obj.trackId));
  };
  const shouldUseMp3 = () => {
    return isIOS() || isSafari();
  };
  const favoritesTracks = getFavoritesTracks(favoritesState);

  const wavesurfer = useWavesurfer({
    containerRef,
    url: shouldUseMp3() ? bottomTrackData?.lowQualityFileUrl : bottomTrackData?.streamingFileUrl,
    peaks: bottomTrackData?.peaks,
    // Clicking the waveform seeks (native) AND starts playing from there.
    onInteraction: (ws) => {
      if (!isUserCanPlay) return setOpenLoginDialog();
      ws.play().catch((e) => console.log(e, "waveform click play error"));
    },
  });

  const [wavUpgrade, setWavUpgrade] = useState(false);
  const [state, dispatchReducer] = useReducer(waveSurferPlayerReducer, {
    isFavorite: false,
    isLoading: true,
    activeStep: 1,
    openModal: false,
    openLicenseModal: false,
    isPlaying: false,
    isCopyright: false,
    volume: getVolume(),
    isMuted: getIsMuted(),
  });
  const { isLoggedIn } = useAuth();

  const isVolumeTouched = getVolume() !== MAX_VOLUME && !getIsMuted();

  const onPlayClick = useCallback(() => {
    if (!isUserCanPlay) return setOpenLoginDialog();
    if (!wavesurfer) return;

    wavesurfer.playPause();
  }, [wavesurfer, isUserCanPlay]);

  // Spacebar toggles the bottom player, the way every media player does — but
  // only when a track is actually loaded, and never while the visitor is typing
  // or has an interactive element focused (Space there is that element's job,
  // and would otherwise both scroll the page and double-fire a focused button).
  useEffect(() => {
    if (!id) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.code !== "Space" && e.key !== " ") return;
      const el = e.target as HTMLElement | null;
      const tag = el?.tagName;
      if (
        tag === "INPUT" ||
        tag === "TEXTAREA" ||
        tag === "SELECT" ||
        tag === "BUTTON" ||
        tag === "A" ||
        el?.isContentEditable
      ) {
        return;
      }
      e.preventDefault();
      onPlayClick();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [id, onPlayClick]);

  const getTrack = async () => {
    await dispatch(bottomTrack(slug));
  };

  useResetDownloadModalStep(state, dispatchReducer, EWaveSurferPlayer.ActiveStep);

  useEffect(() => {
    if (location.pathname.includes("track") && wavesurfer) {
      const isPlaying = wavesurfer?.isPlaying();
      if (!isPlaying) {
        const isMuted = wavesurfer?.getMuted();
        const volume = wavesurfer?.getVolume();

        const handlePlay = () => {
          wavesurfer?.pause();
          wavesurfer?.setMuted(isMuted);
          wavesurfer?.setVolume(volume);
        };

        wavesurfer?.setMuted(true);

        const playPromise = wavesurfer?.play();
        if (playPromise !== undefined) {
          playPromise.then(() => handlePlay()).catch((e) => console.log(e, "bottom player component play 1 error"));
        } else {
          handlePlay();
        }
      } else {
        wavesurfer?.play().catch((e) => console.log(e, "bottom player component play 2 error"));
      }
    }
  }, [location]);

  useEffect(() => {
    if (!id) return;
    getTrack().catch(() => handleCloseTrack());
  }, [id]);

  useEffect(() => {
    dispatchReducer({
      type: EWaveSurferPlayer.IsLoading,
      payload: true,
    });
  }, [bottomTrackData?.lowQualityFileUrl, bottomTrackData?.streamingFileUrl]);

  useEffect(() => {
    dispatchReducer({
      type: EWaveSurferPlayer.IsPlaying,
      payload: isPlaying,
    });
    if (!wavesurfer) {
      return;
    }
    if (bottomTrackData._id !== data.id) {
      return;
    }
    if (isPlaying) {
      wavesurfer?.play().catch((e) => console.log(e, "bottom player component play"));
    } else {
      wavesurfer?.pause();
    }
  }, [isPlaying, state.isLoading]);

  useEffect(() => {
    if (!wavesurfer) return;

    const currentTimeHandler = () => {
      if (state.isLoading) return;
      if (currentTimeRef.current) {
        const currentTime = Math.floor(wavesurfer.getCurrentTime());
        currentTimeRef.current.textContent = formatTime(currentTime);
      }
      // drive the phone thin seek line (no-op on desktop where progressRef is unused)
      if (progressRef.current) {
        const dur = wavesurfer.getDuration();
        const cur = wavesurfer.getCurrentTime();
        progressRef.current.style.width = dur > 0 ? `${Math.min(100, (cur / dur) * 100)}%` : "0%";
      }
    };

    const onReady = () => {
      currentTimeHandler();
      dispatchReducer({ type: EWaveSurferPlayer.IsLoading, payload: false });
      // if (isPlaying) wavesurfer.play();
      dispatchReducer({ type: EWaveSurferPlayer.IsPlaying, payload: wavesurfer.isPlaying() });
    };

    const onPlay = () => {
      // Start (or resume) the listen timer for the current track.
      listenRef.current.id = bottomTrackData?._id ?? listenRef.current.id;
      listenRef.current.since = performance.now();
      dispatchReducer({ type: EWaveSurferPlayer.IsPlaying, payload: true });
      setMedia((prev) => {
        const newMedia = wavesurfer.getMediaElement();
        if (!prev || prev !== newMedia) {
          if (prev) {
            prev.pause();
            prev.currentTime = 0;
          }
          return newMedia;
        }
        return prev;
      });
      setPlay(true);
    };

    const onPause = () => {
      // Banked, not sent — the user may resume; the total is flushed on finish /
      // track change / page close.
      ltAccrue();
      dispatchReducer({ type: EWaveSurferPlayer.IsPlaying, payload: false });
      setPlay(false);
    };

    const onFinish = () => {
      // Track played to the end → the listen is complete; send it.
      ltFlush();
    };

    const subscriptions = [
      wavesurfer.on("ready", onReady),
      wavesurfer.on("play", onPlay),
      wavesurfer.on("pause", onPause),
      wavesurfer.on("finish", onFinish),
      wavesurfer.on("audioprocess", currentTimeHandler),
      wavesurfer.on("timeupdate", currentTimeHandler),
    ];

    return () => {
      subscriptions.forEach((unsub) => unsub());
    };
  }, [wavesurfer, bottomTrackData?._id, state.isLoading]);

  // A new track loaded → flush the previous track's banked listen, start fresh.
  useEffect(() => {
    ltFlush();
    listenRef.current = { id: bottomTrackData?._id, secs: 0, since: null };
  }, [bottomTrackData?._id]);

  // Flush any in-progress listen when the page is closed or the player unmounts.
  useEffect(() => {
    const onHide = () => ltFlush(true);
    window.addEventListener("pagehide", onHide);
    return () => {
      window.removeEventListener("pagehide", onHide);
      ltFlush(true);
    };
  }, []);

  useEffect(() => {
    dispatchReducer({
      type: EWaveSurferPlayer.IsFavorite,
      payload: favoritesTracks.has(bottomTrackData?._id),
    });
  }, [favoritesState.length, bottomTrackData?._id]);

  useEffect(() => {
    if (!wavesurfer) return;

    if (!state.isMuted) {
      return wavesurfer.setVolume(state.volume);
    }

    wavesurfer.setVolume(MUTE_VOLUME);
  }, [state.isMuted, state.volume, wavesurfer]);

  useEffect(() => {
    if (!isUserCanPlay) {
      wavesurfer.pause();
      setOpenLoginDialog();
    }
    if (!wavesurfer) return;
  }, [isUserCanPlay]);

  const onVolumeChange = (_e: Event | SyntheticEvent<Element, Event>, value: number | number[]) => {
    if (Array.isArray(value)) return;

    dispatchReducer({
      type: EWaveSurferPlayer.IsMuted,
      payload: !value,
    });
    setMuted(!value);

    dispatchReducer({
      type: EWaveSurferPlayer.Volume,
      payload: value,
    });
    setVolume(value);
    window.dispatchEvent(new CustomEvent("as:volume-sync", { detail: { volume: value, muted: !value } }));
  };

  const handleVolumeMute = () => {
    const newVol = state.isMuted ? 1 : 0;
    const newMuted = !state.isMuted;
    dispatchReducer({ type: EWaveSurferPlayer.Volume, payload: newVol });
    setVolume(newVol);
    dispatchReducer({ type: EWaveSurferPlayer.IsMuted, payload: newMuted });
    setMuted(newMuted);
    window.dispatchEvent(new CustomEvent("as:volume-sync", { detail: { volume: newVol, muted: newMuted } }));
  };

  // Listen for volume changes from catalog slider
  useEffect(() => {
    const handleSync = (e: Event) => {
      const { volume: vol, muted } = (e as CustomEvent).detail;
      dispatchReducer({ type: EWaveSurferPlayer.Volume, payload: vol });
      dispatchReducer({ type: EWaveSurferPlayer.IsMuted, payload: muted });
    };
    window.addEventListener("as:volume-sync", handleSync);
    return () => window.removeEventListener("as:volume-sync", handleSync);
  }, []);

  const handleNext = () => {
    dispatchReducer({
      type: EWaveSurferPlayer.ActiveStep,
      payload: state.activeStep + 1,
    });
  };

  const whitelistingSuspended = useWhitelistingSuspended();
  const goFixWhitelisting = useWhitelistingPausedRedirect();

  const handleDownload = () => {
    // Purchase/download freeze (ToU §17.4): a paused account can't acquire new
    // tracks or open the buy flow from the player. (Owned re-downloads: /orders.)
    // Send them to the Account Issues modal — a silent return looked broken.
    if (whitelistingSuspended) return goFixWhitelisting();
    const trackTier = (bottomTrackData?.tier || "premium") as "free" | "premium";
    // Owning a license doesn't open the download modal here — premium tracks
    // route Free users to the license/buy flow so they can buy again. Purchased
    // tracks are downloaded from the orders page.
    const showDl =
      accountType === AccountType.SUBSCRIPTION ||
      accountType === AccountType.MUSIC_CREATOR ||
      (accountType === AccountType.FREE && trackTier === "free");
    // Even free downloads require an account — a guest gets the sign-up dialog.
    if (showDl && !isLoggedIn) return setOpenLoginDialog();
    dispatchReducer({
      type: showDl ? EWaveSurferPlayer.OpenModal : EWaveSurferPlayer.OpenLicenseModal,
      payload: showDl ? !state.openModal : !state.openLicenseModal,
    });
  };

  const handleLicensing = () => navigate(makePathForTrack(ERoutes.Licensing, bottomTrackData?._id));

  const handleNavigate = (route: ERoutes, id: string) => {
    if (state.isLoading || !bottomTrackData) return;

    navigate(makePath(route, id));
  };

  const handleNavigateComposer = (composerName: string) => {
    if (state.isLoading || !bottomTrackData) return;
    navigate(navigateToCatalogWithQuery(composerName, FiltersType.COMPOSER));
  };

  const handleLike = async () => {
    if (!isLoggedIn) {
      return setOpenLoginDialog();
    }

    // Optimistic control: it flips before the server answers, so a view has
    // to be turned away here rather than left to show a change that was refused.
    if (refusedWhileViewing()) return;
    const willFavorite = !state.isFavorite;
    // Optimistic: flip the heart immediately, BEFORE the network round-trip,
    // so it doesn't appear to only update after a re-render is forced.
    dispatchReducer({
      type: EWaveSurferPlayer.IsFavorite,
      payload: willFavorite,
    });

    const message = willFavorite ? TrackPhrases.ADDED : TrackPhrases.REMOVED;
    customToast(message, ToastStatuses.SUCCESS);

    await dispatch(
      willFavorite
        ? createFavorites({ trackId: bottomTrackData?._id })
        : deleteFavorites({ trackId: bottomTrackData?._id }),
    );
  };

  const playerProps: TPlayerProps = useMemo(
    () => ({
      bottomTrackData,
      containerRef,
      currentTimeRef,
      progressRef,
      state,
      isVolumeTouched,
      dispatchReducer,
      handleLicensing,
      handleNavigateComposer,
      handleDownload,
      handleNavigate,
      handleVolumeMute,
      onVolumeChange,
      onPlayClick,
      handleLike,
      handleCloseTrack,
      handleSeek: (progress: number) => {
        if (!wavesurfer) return;
        wavesurfer.seekTo(progress);
        // While PAUSED, seekTo can no-op on a MediaElement backend (the
        // position snaps back), so set the media currentTime directly. This
        // moves the position without starting playback.
        const dur = wavesurfer.getDuration();
        const media = wavesurfer.getMediaElement?.();
        if (media && dur > 0) {
          try {
            media.currentTime = Math.max(0, Math.min(dur, progress * dur));
          } catch { /* not seekable yet — ignore */ }
        }
      },
    }),
    [
      bottomTrackData,
      containerRef,
      currentTimeRef,
      progressRef,
      state,
      isVolumeTouched,
      dispatchReducer,
      handleLicensing,
      handleNavigateComposer,
      handleDownload,
      handleNavigate,
      handleVolumeMute,
      onVolumeChange,
      onPlayClick,
      handleLike,
      handleCloseTrack,
    ],
  );

  return (
    <>
      {isDesktop ? (
        <Styled.SongStack direction="row" spacing="16px" width="100%" alignItems="center">
          <DesktopLayout {...playerProps} />
        </Styled.SongStack>
      ) : (
        <MobileLayout {...playerProps} curated={curated} />
      )}
      <NewDownloadModal
        open={state.openModal}
        song={bottomTrackData}
        onClose={() =>
          dispatchReducer({
            type: EWaveSurferPlayer.OpenModal,
            payload: !state.openModal,
          })
        }
        onOpenLicense={() => {
          dispatchReducer({ type: EWaveSurferPlayer.OpenModal, payload: false });
          setWavUpgrade(true);
          setTimeout(() => {
            dispatchReducer({ type: EWaveSurferPlayer.OpenLicenseModal, payload: true });
          }, 300);
        }}
        copyrightModal={state.isCopyright}
        activeStep={state.activeStep}
        handleNext={handleNext}
        composer={getTitleForDownloading(bottomTrackData)}
        videoUrl={bottomTrackData?.videoUrl}
        spotifyUrl={bottomTrackData?.spotifyUrl}
        cover={bottomTrackData?.mediumCoverUrl || bottomTrackData?.largeCoverUrl}
      />
      {bottomTrackData && (
        <LicenseModal
          open={state.openLicenseModal}
          syncUrl={false}
          onClose={() => {
            dispatchReducer({ type: EWaveSurferPlayer.OpenLicenseModal, payload: false });
            setWavUpgrade(false);
          }}
          trackName={bottomTrackData.name}
          trackId={bottomTrackData._id}
          trackSlug={bottomTrackData.slug}
          composerName={
            bottomTrackData.composer
              ? typeof bottomTrackData.composer === "string"
                ? bottomTrackData.composer
                : (bottomTrackData.composer as any)?.name || ""
              : ""
          }
          coverUrl={bottomTrackData.smallCoverUrl || bottomTrackData.mediumCoverUrl || bottomTrackData.largeCoverUrl}
          isFreeUse={bottomTrackData.tier === "free"}
          tier={bottomTrackData.tier || "premium"}
          licenseTier={(bottomTrackData as any).licenseTier}
          wavUpgrade={wavUpgrade}
        />
      )}
    </>
  );
};
